
import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faMoon,
    faSun,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import UserDropdown from "./UserDropdown.tsx";
import { useTranslation } from 'react-i18next';
import CompactModeToggle from './CompactModeToggle';

interface AppHeaderProps {
    title: string;
    subtitle?: string;
    icon: IconDefinition;
    backTo?: string;
    darkMode: boolean;
    onToggleDarkMode: () => void;
    rightActions?: React.ReactNode;
}

const AppHeader: React.FC<AppHeaderProps> = ({
                                                 title,
                                                 subtitle,
                                                 icon,
                                                 backTo = '/',
                                                 darkMode,
                                                 onToggleDarkMode,
                                                 rightActions
                                             }) => {
    const { t } = useTranslation();

    return (
        <header className="glass-header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Left side - Back & Title */}
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <Link
                            to={backTo}
                            className="glass-header-back"
                            title={t('components.appHeader.backToHome')}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </Link>

                        <div className="flex items-center space-x-3 min-w-0">
                            <div className={`glass-header-icon w-10 h-10 flex-shrink-0 ring-2 ring-white/30 dark:ring-white/10`}>
                                <FontAwesomeIcon icon={icon} className="text-white relative z-10" />
                            </div>
                            <div className="min-w-0">
                                <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight truncate">
                                    {title}
                                </h1>
                                {subtitle && (
                                    <p className="hidden sm:block text-xs text-gray-400 dark:text-gray-500 truncate">
                                        {subtitle}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right side - Actions — gap rather than space-x, which
                        would leave a margin behind whatever is hidden on phones */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Custom actions */}
                        {rightActions}

                        <CompactModeToggle />

                        {/* Duplicated by the dark-mode item inside UserDropdown,
                            so it gives up its space on narrow screens */}
                        <button
                            onClick={onToggleDarkMode}
                            className="glass-header-back !p-2.5 hidden sm:block"
                            title={darkMode ? 'Light mode' : 'Dark mode'}
                        >
                            <FontAwesomeIcon icon={darkMode ? faSun : faMoon} className="text-sm" />
                        </button>

                        {/* User info */}
                        <UserDropdown darkMode={darkMode} onToggleDarkMode={onToggleDarkMode} />
                    </div>
                </div>
            </div>
        </header>
    );
};

export default AppHeader;